import { useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { Person } from "@/types";
import { renderName } from "@/lib/personHelpers";
import { ProfileSkeleton } from "@/components/Skeleton";
import { PERSON_DETAILS_HEADER_QUERY } from "@/pages/people/[pgaId]/queries";
import ObjectHeading from "@/components/ObjectHeading";
import { useOutletContext } from "react-router-dom";
import type { ApolloError } from "@apollo/client";
import { PDRHistoryDetailsData } from "./types";
import {
  ParticipationDetailContent,
  isCcmsLegacyPdrCredit,
  isTypeLevelPdrParticipation,
  mapParticipationRateFields,
  CCMS_LEGACY_MANAGING_SECTION_NAME
} from "@/features/participation";
import type { ParticipationDetailData } from "@/features/participation";

type PersonData = { person: Person };

export const ObjectHeadingWrapper = ({ pgaId, category }: { pgaId: string; category: string }) => {
  const { data, loading, error } = useQuery<PersonData>(PERSON_DETAILS_HEADER_QUERY, {
    variables: { pgaId }
  });

  if (loading) return <ProfileSkeleton />;
  if (error) return <div>Error: {error.message}</div>;

  const { person } = data!;
  const { profilePhoto, displayName, informalName, preferredLastName, suffixName, middleName, firstName } = person;

  const metaData = [{ text: `PGA ID: ${pgaId}` }, { text: category }];
  const title = renderName(displayName, informalName, preferredLastName, suffixName, middleName, firstName);

  return <ObjectHeading title={title} imageUrl={profilePhoto} iconName="person" isDetailsView metaData={metaData} />;
};

function toParticipationDetailData(
  activity: PDRHistoryDetailsData["pdrHistoryDetails"] | undefined
): ParticipationDetailData {
  if (!activity) return { startDate: "", endDate: "" };

  const common: ParticipationDetailData = {
    title: activity.description1,
    description: activity.description2,
    creditDescription2: activity.description2,
    creditsAwarded: activity.creditsAccepted,
    creditsAppliedFor: activity.creditsAppliedFor,
    startDate: activity.participationDates?.startedOn,
    endDate: activity.participationDates?.endedOn,
    location: activity.location || undefined,
    submittedAt: activity.submittedAt,
    author: activity.author,
    activityCode: activity.activityCode,
    creditType: activity.creditType,
    category: activity.category
  };

  if (isCcmsLegacyPdrCredit(activity)) {
    return {
      ...common,
      managingSection: { name: CCMS_LEGACY_MANAGING_SECTION_NAME },
      legacyActivityDetails: {
        activityTitle: activity.description1 || undefined,
        activityType: activity.category || undefined
      }
    };
  }

  const participation = activity.activityParticipation;
  if (!participation) {
    return common;
  }

  return {
    ...common,
    ...mapParticipationRateFields({
      role: participation.role,
      attendanceType: participation.attendanceType,
      quantity: participation.quantity,
      isTypeLevelParticipation: isTypeLevelPdrParticipation(activity),
      activityType: participation.activityType ?? participation.activity?.activityType,
      participationActivity: participation.activity,
      participationTitle: participation.title ?? activity.description1,
      series: undefined
    })
  };
}

export default function EducationActivityDetailPage() {
  const { pgaId = "", activityId = "" } = useParams();

  const { data, loading, error } = useOutletContext<{
    data: PDRHistoryDetailsData;
    loading: boolean;
    error: ApolloError;
  }>();
  const activity = data?.pdrHistoryDetails;

  if (loading) return <ProfileSkeleton />;
  if (error) return <div>Error: {error.message}</div>;
  if (!activityId || !activity) return null;

  const participationDetailData = toParticipationDetailData(activity);

  return <ParticipationDetailContent pgaId={pgaId} participation={participationDetailData} />;
}
